import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";

// Placeholder for item definitions - ideally move this to a shared config
const itemDefinitions = {
  avatar_knight: {
    name: "Knight Avatar",
    type: "avatar",
    avatarProps: {
      avatar_hair_type: "short",
      avatar_hair: "#A9A9A9",
      avatar_shirt: "#808080",
      avatar_eyes: "#0000FF",
      avatar_skin: "#FFE0B2",
    },
  },
  avatar_wizard: {
    name: "Wizard Avatar",
    type: "avatar",
    avatarProps: {
      avatar_hair_type: "long",
      avatar_hair: "#FFFFFF",
      avatar_shirt: "#9C27B0",
      avatar_eyes: "#FFD700",
      avatar_skin: "#FFCCBC",
    },
  },
  // Option to clear background (no border)
  bg_none: {
    name: "None",
    type: "background",
    previewImageUrl: null,
  },
  bg_castle: {
    name: "Castle Background",
    type: "background",
    previewImageUrl: "/border-horizontal.png", // horizontal tile example
  },
  bg_forest: {
    name: "Enchanted Forest",
    type: "background",
    previewImageUrl: "/bg_forest.png", // forest full image
  },
  // Add a representation for the default avatar
  avatar_default: {
    name: "Default Look",
    type: "avatar",
    avatarProps: {
      avatar_hair_type: "medium",
      avatar_hair: "#6D4C41",
      avatar_shirt: "#2196F3",
      avatar_eyes: "#795548",
      avatar_skin: "#FFF9C4",
    },
  },
  // Add border themes
  border_none: {
    id: "border_none",
    name: "None",
    type: "borders",
    previewImage: null,
  },
  border_default: {
    id: "border_default",
    name: "Default Border",
    type: "borders",
    previewImage: "/borders/default/border-left.png",
  },
  border_castle: {
    id: "border_castle",
    name: "Castle Borders",
    description: "Medieval castle-themed borders for your math workspace",
    type: "borders",
    previewImage: "/borders/castle/border-left.png",
  },
  border_forest: {
    id: "border_forest",
    name: "Forest Borders",
    description: "Enchanted forest-themed borders for your math workspace",
    type: "borders",
    previewImage: "/borders/forest/border-left.png",
  },
  border_runic: {
    id: "border_runic",
    name: "Runic Borders",
    description: "Mystical runic-themed borders for your math workspace",
    type: "borders",
    previewImage: "/borders/runic/border-left.png",
  },
  border_bookend: {
    id: "border_bookend",
    name: "Bookend Borders",
    description: "Classic bookend-themed borders for your math workspace",
    type: "borders",
    previewImage: "/borders/bookend/border-left.png",
  },
};

const Inventory = ({ onClose }) => {
  const { user, updateUserData, supabase } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("avatars");
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    const fetchInventory = async () => {
      if (!user || !supabase) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("user_inventory")
          .select("item_id, item_type")
          .eq("user_id", user.id);

        if (error) throw error;

        // Normalize item_type from DB to match our category ids
        const normalizedData = data.map(({ item_id, item_type }) => {
          let typeKey = item_type;
          if (item_type === "avatar") typeKey = "avatars";
          else if (item_type === "background") typeKey = "backgrounds";
          else if (item_type === "border") typeKey = "borders";
          return { item_id, item_type: typeKey };
        });
        // Add the 'default' avatar and 'None' background placeholders
        const items = [
          ...normalizedData,
          { item_id: "avatar_default", item_type: "avatars" },
          { item_id: "bg_none", item_type: "backgrounds" },
          { item_id: "border_none", item_type: "borders" },
        ];
        console.log(
          "Inventory items:",
          items,
          "Selected tab:",
          selectedCategory
        );
        setInventory(items);
      } catch (error) {
        console.error("Error fetching inventory:", error);
        setStatusMessage({
          success: false,
          message: "Failed to load inventory.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [user, supabase]);

  const handleEquip = async (item_id) => {
    const itemDef = itemDefinitions[item_id];
    // Basic validation: Check if item definition exists and is an avatar/background/border
    if (
      !itemDef ||
      (itemDef.type !== "avatar" &&
        itemDef.type !== "background" &&
        itemDef.type !== "borders")
    ) {
      console.error(
        "Cannot equip item: Invalid definition or not an avatar/background/border",
        item_id
      );
      setStatusMessage({ success: false, message: "Cannot equip this item." });
      setTimeout(() => setStatusMessage(null), 3000);
      return;
    }

    let updates = {};
    let successMessage = "";

    // Special handling for default avatar
    if (item_id === "avatar_default") {
      updates = {
        avatar_hair_type: "medium",
        avatar_hair: "#6D4C41",
        avatar_shirt: "#2196F3",
        avatar_eyes: "#795548",
        avatar_skin: "#FFF9C4",
      };
      successMessage = "Default look equipped!";
    }
    // Handling for purchased avatars
    else if (itemDef.avatarProps) {
      updates = {
        avatar_hair_type: itemDef.avatarProps.avatar_hair_type,
        avatar_hair: itemDef.avatarProps.avatar_hair,
        avatar_shirt: itemDef.avatarProps.avatar_shirt,
        avatar_eyes: itemDef.avatarProps.avatar_eyes,
        avatar_skin: itemDef.avatarProps.avatar_skin,
      };
      successMessage = `${itemDef.name} equipped!`;
    }
    // Handling for backgrounds
    else if (itemDef.type === "background") {
      updates = { profile_background: itemDef.previewImageUrl };
      successMessage = `${itemDef.name} equipped!`;
    }
    // Handling for borders
    else if (itemDef.type === "borders") {
      if (item_id === "border_none") {
        updates = { selected_border_theme: null };
        successMessage = "Borders removed!";
      } else {
        const themeName = item_id.replace("border_", "");
        updates = { selected_border_theme: themeName };
        successMessage = `${itemDef.name} equipped!`;
      }
    }
    // If it's an avatar but doesn't have avatarProps (error case)
    else {
      console.error(
        "Cannot equip item: Missing required properties for",
        item_id
      );
      setStatusMessage({ success: false, message: "Item data is missing." });
      setTimeout(() => setStatusMessage(null), 3000);
      return;
    }

    // Attempt to update user data in the database
    try {
      console.log(`Equipping item ${item_id}:`, updates);
      await updateUserData(updates);
      console.log("Successfully equipped item:", item_id);
      setStatusMessage({ success: true, message: successMessage });
    } catch (error) {
      console.error("Error equipping item:", item_id, error);
      setStatusMessage({ success: false, message: "Failed to equip item." });
    }

    // Clear status message after delay
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const categories = [
    { id: "avatars", name: "Avatars" },
    { id: "backgrounds", name: "Backgrounds" },
    { id: "borders", name: "Borders" },
  ];

  // Filter items by selected category
  const filteredInventory = inventory.filter(
    (item) => item.item_type === selectedCategory
  );

  // Order inventory so 'None' options appear first in their respective tabs
  let renderedInventory = filteredInventory;
  if (selectedCategory === "backgrounds") {
    const noneItem = renderedInventory.find(
      (item) => item.item_id === "bg_none"
    );
    const otherItems = renderedInventory.filter(
      (item) => item.item_id !== "bg_none"
    );
    renderedInventory = noneItem ? [noneItem, ...otherItems] : otherItems;
  } else if (selectedCategory === "borders") {
    const noneItem = renderedInventory.find(
      (item) => item.item_id === "border_none"
    );
    const otherItems = renderedInventory.filter(
      (item) => item.item_id !== "border_none"
    );
    renderedInventory = noneItem ? [noneItem, ...otherItems] : otherItems;
  }

  return (
    <div className="inventory-overlay" onClick={onClose}>
      <style jsx>{`
        /* Basic styles - copy/adapt from GeldStore.css */
        .inventory-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .inventory {
          background-color: white;
          border-radius: 16px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          padding: 20px;
          position: relative;
        }
        .inventory-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #eee;
          padding-bottom: 10px;
        }
        .inventory-title {
          font-size: 24px;
          font-weight: 700;
        }
        .close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }
        .category-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          overflow-x: auto;
          padding-bottom: 5px;
        }
        .category-tab {
          background-color: #f5f5f5;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          white-space: nowrap;
          font-weight: 500;
          transition: all 0.2s;
        }
        .category-tab.active {
          background-color: #e0e0e0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .inventory-items {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 20px;
        }
        .inventory-item {
          border: 1px solid #eee;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between; /* push preview and name up and button down */
        }
        .item-preview {
          width: 100px;
          height: 100px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f0f0f0;
          border-radius: 8px;
          overflow: hidden;
        }
        .item-name {
          font-weight: 600;
          margin-bottom: 10px;
          text-align: center;
        }
        .equip-button {
          background-color: #4caf50;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          margin-top: 10px;
          font-weight: 500;
          width: 100%;
        }
        .equip-button:disabled {
          background-color: #cccccc;
        }
        .status-message {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 10px 20px;
          border-radius: 8px;
          color: white;
          font-weight: 500;
          z-index: 2000;
          animation: fadeIn 0.3s ease-out;
        }
        .status-message.success {
          background-color: #4caf50;
        }
        .status-message.error {
          background-color: #f44336;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
      <div className="inventory" onClick={(e) => e.stopPropagation()}>
        <div className="inventory-header">
          <h2 className="inventory-title">My Items</h2>
        </div>
        <button className="close-button" onClick={onClose}>
          &times;
        </button>

        <div className="category-tabs">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`category-tab ${
                selectedCategory === category.id ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </div>
          ))}
        </div>

        {loading ? (
          <p>Loading inventory...</p>
        ) : (
          <div className="inventory-items">
            {renderedInventory.length === 0 ? (
              <p>No items in this category yet. Visit the store!</p>
            ) : (
              renderedInventory.map(({ item_id, item_type }) => {
                const itemDef = itemDefinitions[item_id];
                if (!itemDef) return null; // Skip if definition is missing

                // Check if the current item is equipped (using DB column names)
                const isEquipped =
                  itemDef.type === "avatar" &&
                  user?.avatar_hair_type ===
                    itemDef.avatarProps?.avatar_hair_type &&
                  user?.avatar_hair === itemDef.avatarProps?.avatar_hair &&
                  user?.avatar_shirt === itemDef.avatarProps?.avatar_shirt &&
                  user?.avatar_eyes === itemDef.avatarProps?.avatar_eyes &&
                  user?.avatar_skin === itemDef.avatarProps?.avatar_skin;

                // Special check for default avatar (using DB column names and default values)
                const isDefaultEquipped =
                  item_id === "avatar_default" &&
                  user?.avatar_hair_type === "medium" &&
                  user?.avatar_hair === "#6D4C41" &&
                  user?.avatar_shirt === "#2196F3" &&
                  user?.avatar_eyes === "#795548" &&
                  user?.avatar_skin === "#FFF9C4";

                return (
                  <div key={item_id} className="inventory-item">
                    <div className="item-preview">
                      {itemDef.type === "avatar" && itemDef.avatarProps ? (
                        <UserAvatar user={itemDef.avatarProps} size={80} />
                      ) : itemDef.type === "background" ? (
                        itemDef.previewImageUrl ? (
                          <div
                            style={{
                              backgroundImage: `url(${itemDef.previewImageUrl})`,
                              backgroundSize: "cover",
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        ) : (
                          // 'None' background placeholder (transparent)
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              background: "transparent",
                            }}
                          />
                        )
                      ) : itemDef.type === "borders" ? (
                        itemDef.previewImage ? (
                          <div
                            style={{
                              backgroundImage: `url(${itemDef.previewImage})`,
                              backgroundSize: "contain",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                              width: "100%",
                              height: "100%",
                              backgroundColor: "#FFFFFF",
                            }}
                          />
                        ) : (
                          // 'None' border placeholder (transparent)
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              background: "transparent",
                            }}
                          />
                        )
                      ) : (
                        <span>?</span>
                      )}
                    </div>
                    <div className="item-name">{itemDef.name}</div>
                    <button
                      className="equip-button"
                      onClick={() => handleEquip(item_id)}
                      disabled={
                        (itemDef.type === "avatar" &&
                          ((item_id === "avatar_default" &&
                            isDefaultEquipped) ||
                            (item_id !== "avatar_default" && isEquipped))) ||
                        (itemDef.type === "background" &&
                          user?.profile_background ===
                            itemDef.previewImageUrl) ||
                        (itemDef.type === "borders" &&
                          ((item_id === "border_none" &&
                            !user?.selected_border_theme) ||
                            (item_id !== "border_none" &&
                              user?.selected_border_theme ===
                                item_id.replace("border_", ""))))
                      }
                    >
                      {(itemDef.type === "avatar" &&
                        ((item_id === "avatar_default" && isDefaultEquipped) ||
                          (item_id !== "avatar_default" && isEquipped))) ||
                      (itemDef.type === "background" &&
                        user?.profile_background === itemDef.previewImageUrl) ||
                      (itemDef.type === "borders" &&
                        ((item_id === "border_none" &&
                          !user?.selected_border_theme) ||
                          (item_id !== "border_none" &&
                            user?.selected_border_theme ===
                              item_id.replace("border_", ""))))
                        ? "Equipped"
                        : "Equip"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
        {statusMessage && (
          <div
            className={`status-message ${
              statusMessage.success ? "success" : "error"
            }`}
          >
            {statusMessage.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
